import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  Chip,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { AppContext } from "../../../context/AppContext";

const PRIMARY = "#1565C0";
const PRIMARY_DARK = "#0D47A1";

const headerRowSx = { backgroundColor: PRIMARY };
const headerCellSx = { color: "#fff", fontWeight: 500 };

const asMoney = (v) => {
  const s = String(v ?? "").replace(",", ".").trim();
  if (s === "" || s === ".") return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
const isDigits = (s) => /^\d+$/.test(String(s || ""));

const normalizeMoneyInput = (v) => {
  let s = String(v ?? "").replace(/,/g, ".");
  s = s.replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  return s;
};

const altIdentificacion = (idn) => {
  const s = String(idn || "").trim();
  if (!s) return "";
  if (isDigits(s) && s.length === 10) return s + "001";
  if (isDigits(s) && s.length === 13 && s.endsWith("001")) return s.slice(0, 10);
  return "";
};

const docPrefix = (tipoDoc) => {
  const t = String(tipoDoc || "").toUpperCase().trim();
  if (t === "FACTURA" || t === "01") return "FAC";
  if (t === "NOTA_CREDITO" || t === "04") return "NC";
  if (t === "NOTA_DEBITO" || t === "05") return "ND";
  if (t === "GUIA_REMISION" || t === "06") return "GR";
  if (t === "RETENCION" || t === "07") return "RET";
  return "";
};

const formatDocCell = (tipoDoc, numero) => {
  const n = String(numero || "").trim();
  const p = docPrefix(tipoDoc);
  if (!n) return "";
  return p ? `${p} ${n}` : n;
};

const onlyDate = (v) => {
  const s = String(v || "").trim();
  if (!s) return "";
  return s.split("T")[0].split(" ")[0];
};

const docsFromPayment = (p) => {
  const detalles = Array.isArray(p?.detalles) ? p.detalles : [];

  if (!detalles.length) {
    return String(p?.numero_documento || p?.documento || "").trim();
  }

  return detalles
    .map((d) => {
      const numero = String(d?.numero_documento || d?.documento || d?.doc_id || "").trim();
      const tipoDoc = String(d?.tipo_documento || "").trim();
      return formatDocCell(tipoDoc, numero);
    })
    .filter(Boolean)
    .join(", ");
};

const getRolByTipo = (tipo) => {
  const t = String(tipo || "").toUpperCase().trim();
  if (t === "COBRO" || t === "VENTA") return "CLIENTE";
  return "PROVEEDOR";
};

const getDestinoPayloadKey = (row, tipo) => {
  const origen = String(row?.origen || "").toUpperCase().trim();
  const tipoLocal = String(tipo || "").toUpperCase().trim();

  if (origen === "VENTA" || tipoLocal === "COBRO" || tipoLocal === "VENTA") return "documento_destino_venta_id";
  if (origen === "COMPRA" || tipoLocal === "PAGO" || tipoLocal === "COMPRA") return "documento_destino_compra_id";

  return "";
};

const getErrorText = (j, fallback = "No se pudo procesar.") => {
  const err = j?.error || j?.detail || fallback;

  if (Array.isArray(err)) return err.join(" ");
  if (typeof err === "object" && err !== null) return Object.values(err).flat().join(" ");

  return String(err || fallback);
};

const DocumentPaymentDetail = ({
  value = [],
  onChange,
  identificacion = "",
  personaId = "",
  tipo = "PAGO",
}) => {
  const { accessToken } = useContext(AppContext);

  const [rows, setRows] = useState(Array.isArray(value) ? value : []);
  const [openModal, setOpenModal] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docs, setDocs] = useState([]);
  const [activeRowId, setActiveRowId] = useState(null);

  const [openHist, setOpenHist] = useState(false);
  const [loadingHist, setLoadingHist] = useState(false);
  const [hist, setHist] = useState([]);
  const [histDetail, setHistDetail] = useState(null);

  const [loadingCredito, setLoadingCredito] = useState(false);
  const [creditoDisponible, setCreditoDisponible] = useState(0);
  const [deudaCartera, setDeudaCartera] = useState(0);
  const [personaIdCredito, setPersonaIdCredito] = useState("");
  const [aplicandoCreditoId, setAplicandoCreditoId] = useState("");

  const [confirmCruce, setConfirmCruce] = useState({
    open: false,
    row: null,
    valor: 0,
    documento: "",
  });

  const [resultModal, setResultModal] = useState({
    open: false,
    ok: false,
    title: "",
    message: "",
  });

  const reqRef = useRef(0);
  const creditoReqRef = useRef(0);
  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const onChangeRef = useRef(onChange);

  const rol = useMemo(() => getRolByTipo(tipo), [tipo]);

  const closeResultModal = () => {
    setResultModal({
      open: false,
      ok: false,
      title: "",
      message: "",
    });
  };

  const closeConfirmCruce = () => {
    if (aplicandoCreditoId) return;
    setConfirmCruce({
      open: false,
      row: null,
      valor: 0,
      documento: "",
    });
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try {
        abortRef.current?.abort?.();
      } catch {}
    };
  }, []);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setRows(Array.isArray(value) ? value : []);
  }, [value]);

  useEffect(() => {
    onChangeRef.current?.(rows);
  }, [rows]);

  useEffect(() => {
    reqRef.current += 1;
    try {
      abortRef.current?.abort?.();
    } catch {}
    setOpenModal(false);
    setDocs([]);
    setActiveRowId(null);
    setLoadingDocs(false);
    setOpenHist(false);
    setHist([]);
    setHistDetail(null);
    setLoadingHist(false);
    setConfirmCruce({
      open: false,
      row: null,
      valor: 0,
      documento: "",
    });
  }, [tipo, identificacion, personaId]);

  const authFetch = async (url, options = {}) => {
    const headers = {
      Accept: "application/json",
      ...(options.headers || {}),
    };

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    return fetch(url, {
      ...options,
      credentials: "omit",
      headers,
    });
  };

  const fetchCredito = async () => {
    const pid = String(personaId || "").trim();
    const idn = String(identificacion || "").trim();
    const tipoLocal = String(tipo || "").toUpperCase().trim();

    if (!pid && !idn) {
      setCreditoDisponible(0);
      setDeudaCartera(0);
      setPersonaIdCredito("");
      return;
    }

    const myReq = ++creditoReqRef.current;

    setLoadingCredito(true);

    try {
      const qs = new URLSearchParams();

      if (pid) qs.set("persona_id", pid);
      if (idn) qs.set("identificacion", idn);
      if (tipoLocal) qs.set("tipo", tipoLocal);

      const r = await authFetch(`/cuentas/pagos/credito-persona/?${qs.toString()}`, {
        method: "GET",
      });

      const ct = (r.headers.get("content-type") || "").toLowerCase();

      if (!ct.includes("application/json")) {
        throw new Error("RESP_NOT_JSON");
      }

      const j = await r.json();

      if (!r.ok || !j?.ok) {
        throw new Error(j?.error || "No se pudo cargar el crédito disponible.");
      }

      if (!mountedRef.current || creditoReqRef.current !== myReq) return;

      setCreditoDisponible(asMoney(j?.resumen?.credito_cartera || 0));
      setDeudaCartera(asMoney(j?.resumen?.deuda_cartera || 0));
      setPersonaIdCredito(String(j?.persona?.persona_id || j?.persona?.id || pid || "").trim());
    } catch (e) {
      if (!mountedRef.current || creditoReqRef.current !== myReq) return;

      setCreditoDisponible(0);
      setDeudaCartera(0);
      setPersonaIdCredito("");

      setResultModal({
        open: true,
        ok: false,
        title: "Error cargando crédito",
        message: e?.message === "RESP_NOT_JSON" ? "La respuesta del servidor no es válida." : e?.message || "No se pudo cargar el crédito disponible.",
      });
    } finally {
      if (!mountedRef.current || creditoReqRef.current !== myReq) return;
      setLoadingCredito(false);
    }
  };

  useEffect(() => {
    setCreditoDisponible(0);
    setDeudaCartera(0);
    setPersonaIdCredito("");
    fetchCredito();
  }, [personaId, identificacion, rol]);

  const addRow = () => {
    setRows((p) => [
      ...p,
      {
        id: crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        doc_id: "",
        documento: "",
        fecha_emision: "",
        tipo_documento: "",
        persona: "",
        valor: 0,
        total: 0,
        pagado: 0,
        saldo: 0,
        pago: "",
        origen: "",
      },
    ]);
  };

  const removeRow = (id) => {
    setRows((p) => p.filter((r) => r.id !== id));
  };

  const updatePagoInput = (id, v) => {
    const next = normalizeMoneyInput(v);
    setRows((p) =>
      p.map((r) => {
        if (r.id !== id) return r;
        return { ...r, pago: next };
      })
    );
  };

  const commitPagoInput = (id, v) => {
    setRows((p) =>
      p.map((r) => {
        if (r.id !== id) return r;
        const saldo = asMoney(r.saldo || 0);
        const raw = normalizeMoneyInput(v);
        if (raw === "") return { ...r, pago: "" };
        if (raw === ".") return { ...r, pago: "" };
        const num = asMoney(raw);
        const fixed = clamp(num, 0, saldo);
        return { ...r, pago: fixed.toFixed(2) };
      })
    );
  };

  const copySaldoToPago = (id) => {
    setRows((p) =>
      p.map((r) => {
        if (r.id !== id) return r;
        const saldo = asMoney(r.saldo || 0);
        return { ...r, pago: saldo.toFixed(2) };
      })
    );
  };

  const totalPago = useMemo(() => rows.reduce((acc, r) => acc + asMoney(r.pago || 0), 0), [rows]);

  const buildDocumentosUrl = (idn, tipoLocal, personaIdLocal) => {
    const qs = new URLSearchParams();
    qs.set("tipo", String(tipoLocal || "").toUpperCase());

    if (personaIdLocal) {
      qs.set("persona_id", personaIdLocal);
    }

    if (idn) {
      qs.set("identificacion", idn);
    }

    return `/cuentas/documentos-por-persona/?${qs.toString()}`;
  };

  const doFetch = async (idn, tipoLocal, personaIdLocal, signal) => {
    const url = buildDocumentosUrl(idn, tipoLocal, personaIdLocal);
    const r = await authFetch(url, { method: "GET", signal });
    const ct = (r.headers.get("content-type") || "").toLowerCase();

    if (!ct.includes("application/json")) throw new Error("NOT_JSON");

    const j = await r.json();

    if (r.status === 401 || r.status === 403) throw new Error(j?.detail || j?.error || "NO_AUTORIZADO");
    if (!r.ok) throw new Error(j?.error || "NO_SE_PUDO_CARGAR");

    const arr = Array.isArray(j?.results) ? j.results : Array.isArray(j) ? j : [];

    return arr.filter((x) => asMoney(x?.saldo || 0) > 0);
  };

  const fetchDocs = async () => {
    const idn = String(identificacion || "").trim();
    const pid = String(personaId || personaIdCredito || "").trim();
    const tipoLocal = String(tipo || "").toUpperCase();

    if ((!idn && !pid) || !accessToken) {
      if (!mountedRef.current) return;
      setDocs([]);
      return;
    }

    const myReq = ++reqRef.current;

    try {
      abortRef.current?.abort?.();
    } catch {}

    const controller = new AbortController();
    abortRef.current = controller;

    if (mountedRef.current) {
      setLoadingDocs(true);
      setDocs([]);
    }

    try {
      let arr = await doFetch(idn, tipoLocal, pid, controller.signal);

      if (arr.length === 0 && !pid && tipoLocal === "PAGO") {
        const alt = altIdentificacion(idn);
        if (alt) {
          const arr2 = await doFetch(alt, tipoLocal, "", controller.signal);
          if (arr2.length) arr = arr2;
        }
      }

      if (!mountedRef.current) return;
      if (reqRef.current !== myReq) return;

      setDocs(arr);
    } catch {
      if (!mountedRef.current) return;
      if (reqRef.current !== myReq) return;
      setDocs([]);
    } finally {
      if (!mountedRef.current) return;
      if (reqRef.current !== myReq) return;
      setLoadingDocs(false);
    }
  };

  const openPicker = (rowId) => {
    setActiveRowId(rowId);
    setOpenModal(true);
    fetchDocs();
  };

  const pickDoc = (d) => {
    if (!activeRowId) return;

    const docId = String(d?.id || d?.doc_id || "");
    const numero = String(d?.documento || d?.numero_documento || "");
    const fecha = String(d?.fecha_emision || "");
    const persona = String(d?.persona || d?.comprador_razon_social || d?.razon_social || "");
    const origen = String(d?.origen || "");
    const tipoDoc = String(d?.tipo_documento || "");
    const total = asMoney(d?.total ?? d?.valor_total ?? d?.valor ?? 0);
    const pagado = asMoney(d?.pagado ?? 0);
    const saldo = asMoney(d?.saldo ?? d?.saldo_calc ?? 0);

    if (saldo <= 0) return;

    setRows((prev) => {
      const exists = prev.some((x) => String(x.doc_id || "") !== "" && String(x.doc_id) === docId);

      return prev.map((r) => {
        if (r.id !== activeRowId) return r;
        if (exists && String(r.doc_id || "") !== docId) return r;

        const rawPago = normalizeMoneyInput(r.pago ?? "");
        const pagoActual = rawPago === "" || rawPago === "." ? 0 : asMoney(rawPago);
        const pagoClamped = clamp(pagoActual, 0, saldo);

        return {
          ...r,
          doc_id: docId,
          documento: numero,
          fecha_emision: fecha,
          tipo_documento: tipoDoc,
          persona,
          valor: total,
          total,
          pagado,
          saldo,
          origen,
          pago: pagoClamped > 0 ? pagoClamped.toFixed(2) : saldo.toFixed(2),
        };
      });
    });

    setOpenModal(false);
    setActiveRowId(null);
  };

  const buildHistorialUrl = () => {
    const qs = new URLSearchParams();
    qs.set("tipo", String(tipo || "").toUpperCase());

    const pid = String(personaId || personaIdCredito || "").trim();

    if (pid) {
      qs.set("persona_id", pid);
    }

    if (String(identificacion || "").trim()) {
      qs.set("identificacion", String(identificacion || "").trim());
    }

    return `/cuentas/pagos/historial/?${qs.toString()}`;
  };

  const fetchHistorial = async () => {
    const idn = String(identificacion || "").trim();
    const pid = String(personaId || personaIdCredito || "").trim();

    if ((!idn && !pid) || !accessToken) return;

    setOpenHist(true);
    setHist([]);
    setHistDetail(null);
    setLoadingHist(true);

    try {
      const url = buildHistorialUrl();
      const r = await authFetch(url, { method: "GET" });
      const ct = (r.headers.get("content-type") || "").toLowerCase();

      if (!ct.includes("application/json")) throw new Error("NOT_JSON");

      const j = await r.json();

      if (r.status === 401 || r.status === 403) throw new Error(j?.detail || j?.error || "NO_AUTORIZADO");
      if (!r.ok) throw new Error(j?.error || "NO_SE_PUDO_CARGAR");

      setHist(Array.isArray(j?.results) ? j.results : []);
    } catch {
      setHist([]);
    } finally {
      setLoadingHist(false);
    }
  };

  const openConfirmCruce = (row) => {
    const saldoDoc = asMoney(row?.saldo || 0);
    const credito = asMoney(creditoDisponible || 0);
    const valor = clamp(Math.min(saldoDoc, credito), 0, saldoDoc);
    const documento = formatDocCell(row?.tipo_documento, row?.documento) || "documento";

    setConfirmCruce({
      open: true,
      row,
      valor,
      documento,
    });
  };

  const aplicarCredito = async (row) => {
    if (!row || aplicandoCreditoId) return;

    const pid = String(personaId || personaIdCredito || "").trim();
    const idn = String(identificacion || "").trim();
    const docId = String(row?.doc_id || "").trim();
    const destinoKey = getDestinoPayloadKey(row, tipo);
    const saldoDoc = asMoney(row?.saldo || 0);
    const credito = asMoney(creditoDisponible || 0);
    const valor = clamp(Math.min(saldoDoc, credito), 0, saldoDoc);

    if (!pid) {
      setResultModal({
        open: true,
        ok: false,
        title: "No se puede aplicar crédito",
        message: "Seleccione una persona válida.",
      });
      return;
    }

    if (!docId || !destinoKey) {
      setResultModal({
        open: true,
        ok: false,
        title: "No se puede aplicar crédito",
        message: "Seleccione un documento pendiente válido.",
      });
      return;
    }

    if (saldoDoc <= 0) {
      setResultModal({
        open: true,
        ok: false,
        title: "No se puede aplicar crédito",
        message: "El documento ya está pagado.",
      });
      return;
    }

    if (credito <= 0) {
      setResultModal({
        open: true,
        ok: false,
        title: "No hay crédito disponible",
        message: "La persona no tiene crédito disponible para cruzar.",
      });
      return;
    }

    if (valor <= 0) {
      setResultModal({
        open: true,
        ok: false,
        title: "No se puede aplicar crédito",
        message: "El valor a cruzar debe ser mayor a cero.",
      });
      return;
    }

    const payload = {
      persona_id: pid,
      identificacion: idn,
      rol,
      valor: valor.toFixed(2),
      descripcion: `Aplicación de crédito a ${formatDocCell(row.tipo_documento, row.documento) || "documento"}`,
      [destinoKey]: docId,
    };

    setAplicandoCreditoId(String(row.id || docId));

    try {
      const r = await authFetch("/cartera/cruces/aplicar-credito/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const ct = (r.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) throw new Error("RESP_NOT_JSON");

      const j = await r.json();

      if (!r.ok) throw new Error(getErrorText(j, "No se pudo aplicar el crédito."));

      const nuevoSaldoDoc = asMoney(j?.documento_destino?.saldo ?? saldoDoc - valor);
      const nuevoPagadoDoc = asMoney(j?.documento_destino?.pagado ?? asMoney(row.pagado || 0) + valor);
      const nuevoCredito = asMoney(j?.saldo?.credito ?? credito - valor);
      const nuevaDeuda = asMoney(j?.saldo?.deuda ?? deudaCartera - valor);

      setCreditoDisponible(nuevoCredito);
      setDeudaCartera(nuevaDeuda);

      setRows((prev) =>
        prev
          .map((r0) => {
            if (String(r0.id) !== String(row.id)) return r0;

            if (nuevoSaldoDoc <= 0) {
              return null;
            }

            return {
              ...r0,
              saldo: nuevoSaldoDoc,
              pagado: nuevoPagadoDoc,
              pago: "",
            };
          })
          .filter(Boolean)
      );

      setDocs((prev) =>
        prev
          .map((d) => {
            const did = String(d?.id || d?.doc_id || "");
            if (did !== docId) return d;

            if (nuevoSaldoDoc <= 0) {
              return null;
            }

            return {
              ...d,
              saldo: nuevoSaldoDoc,
              pagado: nuevoPagadoDoc,
            };
          })
          .filter(Boolean)
      );

      setConfirmCruce({
        open: false,
        row: null,
        valor: 0,
        documento: "",
      });

      setResultModal({
        open: true,
        ok: true,
        title: "Crédito aplicado",
        message: `Se aplicó $${valor.toFixed(2)} de crédito al documento.`,
      });

      fetchCredito();
    } catch (e) {
      setResultModal({
        open: true,
        ok: false,
        title: "No se pudo aplicar crédito",
        message: e?.message === "RESP_NOT_JSON" ? "La respuesta del servidor no es válida." : e?.message || "No se pudo aplicar el crédito.",
      });
    } finally {
      setAplicandoCreditoId("");
    }
  };

  const canSearch = Boolean((String(identificacion || "").trim() || String(personaId || personaIdCredito || "").trim()) && accessToken);
  const canUseCredit = Boolean(String(personaId || personaIdCredito || "").trim() && creditoDisponible > 0);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          border: "1px solid #e5e7eb",
          bgcolor: "#f8fafc",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AccountBalanceWalletIcon sx={{ color: PRIMARY }} />
          <Box>
            <Typography sx={{ fontWeight: 800, color: "#111827" }}>
              Crédito disponible: {loadingCredito ? "Cargando..." : `$${Number(creditoDisponible || 0).toFixed(2)}`}
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: 12 }}>
              Deuda en cartera: ${Number(deudaCartera || 0).toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>Documentos pendientes</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 12 }}>
            Solo se muestran documentos con saldo pendiente.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={fetchHistorial}
          disabled={!canSearch}
          sx={{
            textTransform: "none",
            borderColor: PRIMARY,
            color: PRIMARY,
            "&:hover": {
              borderColor: PRIMARY_DARK,
              color: PRIMARY_DARK,
              bgcolor: "rgba(21,101,192,0.04)",
            },
          }}
        >
          Ver historial
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={headerRowSx}>
            <TableCell sx={headerCellSx} />
            <TableCell sx={headerCellSx}>Documento</TableCell>
            <TableCell sx={headerCellSx}>Fecha Emisión</TableCell>
            <TableCell sx={headerCellSx}>Tipo Documento</TableCell>
            <TableCell sx={headerCellSx} align="right">Total</TableCell>
            <TableCell sx={headerCellSx} align="right">Pagado</TableCell>
            <TableCell sx={headerCellSx} align="right">Saldo</TableCell>
            <TableCell sx={headerCellSx} align="right">Valor a registrar</TableCell>
            <TableCell sx={headerCellSx} align="center">Crédito</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row) => {
            const saldo = asMoney(row.saldo || 0);
            const pagoInput = String(row.pago ?? "");
            const applying = String(aplicandoCreditoId) === String(row.id);
            const valorAplicable = Math.min(saldo, creditoDisponible);

            return (
              <TableRow key={row.id}>
                <TableCell>
                  <IconButton onClick={() => removeRow(row.id)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </TableCell>

                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={formatDocCell(row.tipo_documento, row.documento) || ""}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => openPicker(row.id)} disabled={!accessToken}>
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </TableCell>

                <TableCell>{row.fecha_emision || ""}</TableCell>
                <TableCell>{row.tipo_documento || ""}</TableCell>
                <TableCell align="right">${asMoney(row.total ?? row.valor ?? 0).toFixed(2)}</TableCell>
                <TableCell align="right">${asMoney(row.pagado || 0).toFixed(2)}</TableCell>
                <TableCell align="right">${saldo.toFixed(2)}</TableCell>

                <TableCell align="right">
                  <TextField
                    size="small"
                    type="text"
                    value={pagoInput}
                    onChange={(e) => updatePagoInput(row.id, e.target.value)}
                    onBlur={(e) => commitPagoInput(row.id, e.target.value)}
                    inputProps={{ inputMode: "decimal" }}
                    sx={{ width: 180 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => copySaldoToPago(row.id)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </TableCell>

                <TableCell align="center">
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => openConfirmCruce(row)}
                    disabled={!canUseCredit || !row.doc_id || saldo <= 0 || applying}
                    sx={{
                      textTransform: "none",
                      bgcolor: PRIMARY,
                      "&:hover": { bgcolor: PRIMARY_DARK },
                      minWidth: 130,
                    }}
                  >
                    {applying
                      ? "Aplicando..."
                      : !row.doc_id
                      ? "Seleccione documento"
                      : valorAplicable > 0
                      ? `Cruzar $${valorAplicable.toFixed(2)}`
                      : "Sin crédito"}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}

          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} align="center">
                No hay documentos agregados al pago.
              </TableCell>
            </TableRow>
          )}

          <TableRow>
            <TableCell colSpan={7} align="right">
              <Typography>Total pago real:</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography>${totalPago.toFixed(2)}</Typography>
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>

      <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
        <Button
          variant="contained"
          onClick={addRow}
          sx={{
            textTransform: "none",
            bgcolor: PRIMARY,
            "&:hover": { bgcolor: PRIMARY_DARK },
          }}
          startIcon={<AddIcon />}
        >
          Agregar Detalle
        </Button>
      </Box>

      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 800 }}>Seleccionar Documento Pendiente</DialogTitle>

        <DialogContent>
          {!String(identificacion || "").trim() && !String(personaId || personaIdCredito || "").trim() ? (
            <Typography sx={{ py: 2 }} color="error">
              Seleccione una persona válida.
            </Typography>
          ) : !accessToken ? (
            <Typography sx={{ py: 2 }} color="error">
              Sesión expirada. Inicia sesión nuevamente.
            </Typography>
          ) : loadingDocs ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Buscando documentos pendientes…</Typography>
            </Box>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow sx={headerRowSx}>
                  <TableCell sx={headerCellSx}>Fecha</TableCell>
                  <TableCell sx={headerCellSx}>Documento</TableCell>
                  <TableCell sx={headerCellSx}>Persona</TableCell>
                  <TableCell sx={headerCellSx} align="right">Total</TableCell>
                  <TableCell sx={headerCellSx} align="right">Pagado</TableCell>
                  <TableCell sx={headerCellSx} align="right">Saldo</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {docs.map((d) => {
                  const docId = String(d?.id || d?.doc_id || "");
                  const fecha = onlyDate(d?.fecha_emision || "");
                  const numero = String(d?.documento || d?.numero_documento || "");
                  const tipoDoc = String(d?.tipo_documento || "");
                  const persona = String(d?.persona || d?.comprador_razon_social || d?.razon_social || "");
                  const docCell = formatDocCell(tipoDoc, numero);

                  return (
                    <TableRow
                      key={docId || `${fecha}-${docCell}-${persona}`}
                      hover
                      onClick={() => pickDoc(d)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{fecha}</TableCell>
                      <TableCell>{docCell}</TableCell>
                      <TableCell>{persona}</TableCell>
                      <TableCell align="right">${asMoney(d?.total || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${asMoney(d?.pagado || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={`$${asMoney(d?.saldo || 0).toFixed(2)}`}
                          sx={{
                            fontWeight: 800,
                            color: PRIMARY,
                            borderColor: PRIMARY,
                          }}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}

                {docs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No se encontraron documentos con saldo pendiente para esta persona.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openHist} onClose={() => setOpenHist(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 800 }}>Historial de {String(tipo || "").toUpperCase()}</DialogTitle>

        <DialogContent>
          {!String(identificacion || "").trim() && !String(personaId || personaIdCredito || "").trim() ? (
            <Typography sx={{ py: 2 }} color="error">
              Seleccione una persona válida.
            </Typography>
          ) : !accessToken ? (
            <Typography sx={{ py: 2 }} color="error">
              Sesión expirada. Inicia sesión nuevamente.
            </Typography>
          ) : loadingHist ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Cargando historial…</Typography>
            </Box>
          ) : (
            <>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow sx={headerRowSx}>
                    <TableCell sx={headerCellSx}>Fecha</TableCell>
                    <TableCell sx={headerCellSx}>Forma</TableCell>
                    <TableCell sx={headerCellSx}>Cuenta</TableCell>
                    <TableCell sx={headerCellSx}>Banco</TableCell>
                    <TableCell sx={headerCellSx}>Comprobante</TableCell>
                    <TableCell sx={headerCellSx}>Documento</TableCell>
                    <TableCell sx={headerCellSx} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {hist.map((p) => (
                    <TableRow
                      key={String(p?.id)}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => setHistDetail(p)}
                    >
                      <TableCell>{onlyDate(p?.fecha)}</TableCell>
                      <TableCell>{String(p?.forma || "")}</TableCell>
                      <TableCell>{`${String(p?.cuenta_codigo || "")} ${String(p?.cuenta || "")}`.trim()}</TableCell>
                      <TableCell>{String(p?.banco || "")}</TableCell>
                      <TableCell>{String(p?.comprobante || "")}</TableCell>
                      <TableCell>{docsFromPayment(p)}</TableCell>
                      <TableCell align="right">${asMoney(p?.total || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}

                  {hist.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No hay pagos/cobros registrados para esta persona.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {histDetail && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography sx={{ fontWeight: 800, color: PRIMARY, mb: 1 }}>
                    Detalle del movimiento #{String(histDetail?.id)}
                  </Typography>

                  <Table size="small">
                    <TableHead>
                      <TableRow sx={headerRowSx}>
                        <TableCell sx={headerCellSx}>Origen</TableCell>
                        <TableCell sx={headerCellSx}>Documento</TableCell>
                        <TableCell sx={headerCellSx}>Fecha</TableCell>
                        <TableCell sx={headerCellSx} align="right">Valor aplicado</TableCell>
                        <TableCell sx={headerCellSx} align="right">Saldo documento</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {(Array.isArray(histDetail?.detalles) ? histDetail.detalles : []).map((d, idx) => {
                        const numero = String(d?.numero_documento || d?.documento || d?.doc_id || "");
                        const tipoDoc = String(d?.tipo_documento || "");
                        const label = formatDocCell(tipoDoc, numero);

                        return (
                          <TableRow key={`${d?.origen}-${d?.doc_id}-${idx}`}>
                            <TableCell>{String(d?.origen || "")}</TableCell>
                            <TableCell>{label}</TableCell>
                            <TableCell>{onlyDate(d?.fecha_emision)}</TableCell>
                            <TableCell align="right">${asMoney(d?.valor || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">
                              {d?.saldo !== undefined && d?.saldo !== null && d?.saldo !== ""
                                ? `$${asMoney(d?.saldo || 0).toFixed(2)}`
                                : "-"}
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {!Array.isArray(histDetail?.detalles) || histDetail.detalles.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            Sin detalles.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmCruce.open} onClose={closeConfirmCruce} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900, color: PRIMARY }}>
          Confirmar cruce de cuenta
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#111827", fontWeight: 700 }}>
            ¿Seguro desea hacer el cruce de cuenta?
          </Typography>
          <Typography sx={{ mt: 1.5, color: "#444" }}>
            Documento: <b>{confirmCruce.documento}</b>
          </Typography>
          <Typography sx={{ mt: 0.5, color: "#444" }}>
            Valor a cruzar: <b>${Number(confirmCruce.valor || 0).toFixed(2)}</b>
          </Typography>
          <Typography sx={{ mt: 1.5, color: "#64748b", fontSize: 13 }}>
            Esta acción aplicará el crédito disponible contra el saldo pendiente del documento.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeConfirmCruce}
            variant="outlined"
            disabled={Boolean(aplicandoCreditoId)}
            sx={{
              textTransform: "none",
              borderColor: PRIMARY,
              color: PRIMARY,
              "&:hover": {
                borderColor: PRIMARY_DARK,
                color: PRIMARY_DARK,
                bgcolor: "rgba(21,101,192,0.04)",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => aplicarCredito(confirmCruce.row)}
            variant="contained"
            disabled={Boolean(aplicandoCreditoId)}
            sx={{
              textTransform: "none",
              bgcolor: PRIMARY,
              "&:hover": { bgcolor: PRIMARY_DARK },
              minWidth: 130,
            }}
          >
            {aplicandoCreditoId ? "Cruzando..." : "Sí, cruzar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resultModal.open} onClose={closeResultModal} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800, color: resultModal.ok ? PRIMARY : "#d32f2f" }}>
          {resultModal.title}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1, color: "#444" }}>{resultModal.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeResultModal}
            variant="contained"
            sx={{
              textTransform: "none",
              bgcolor: resultModal.ok ? PRIMARY : "#d32f2f",
              "&:hover": { bgcolor: resultModal.ok ? PRIMARY_DARK : "#b71c1c" },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentPaymentDetail;